 
import { useThemeStore } from '@/stores/ThemeStore'
import { computed, reactive } from 'vue'

interface LayoutConfig {
  preset: string
  primary: string
  surface: string | null
  darkTheme: boolean
  menuMode: 'static' | 'overlay'
}

interface LayoutState {
  staticMenuDesktopInactive: boolean
  overlayMenuActive: boolean
  profileSidebarVisible: boolean
  configSidebarVisible: boolean
  staticMenuMobileActive: boolean
  menuHoverActive: boolean
  activeMenuItem: any | null
  mobileMenu: boolean
}

interface MenuItem {
  value?: any
}

const layoutConfig = reactive<LayoutConfig>({
  preset: 'Aura',
  primary: 'emerald',
  surface: null,
  darkTheme: false,
  menuMode: 'static'
})

const layoutState = reactive<LayoutState>({
  staticMenuDesktopInactive: false,
  overlayMenuActive: false,
  profileSidebarVisible: false,
  configSidebarVisible: false,
  staticMenuMobileActive: false,
  menuHoverActive: false,
  activeMenuItem: null,
  mobileMenu: false
})

export function useLayout() {
  const setActiveMenuItem = (item: MenuItem | any): void => {
    layoutState.activeMenuItem = item.value || item
  }

  const toggleDarkMode = async (): Promise<void> => {
    if (!document.startViewTransition) {
      executeDarkModeToggle()
      return
    }

    const transition = document.startViewTransition(() => {
      executeDarkModeToggle()
      return Promise.resolve()
    })

    await transition.finished
  }

  const executeDarkModeToggle = (): void => {
    layoutConfig.darkTheme = !layoutConfig.darkTheme
    const themeStore = useThemeStore()
    themeStore.updateTheme(layoutConfig.darkTheme)
    document.documentElement.classList.toggle('app-dark')
  }

  const toggleMenu = (): void => {
    if (layoutConfig.menuMode === 'overlay') {
      layoutState.overlayMenuActive = !layoutState.overlayMenuActive
    }

    if (window.innerWidth > 991) {
      layoutState.staticMenuDesktopInactive = !layoutState.staticMenuDesktopInactive
    } else {
      layoutState.staticMenuMobileActive = !layoutState.staticMenuMobileActive
    }
  }

  const isSidebarActive = computed((): boolean => layoutState.overlayMenuActive || layoutState.staticMenuMobileActive)

  const isDarkTheme = computed((): boolean => layoutConfig.darkTheme)

  const getPrimary = computed((): string => layoutConfig.primary)

  const getSurface = computed((): string | null => layoutConfig.surface)

  return {
    layoutConfig,
    layoutState,
    toggleMenu,
    isSidebarActive,
    isDarkTheme,
    getPrimary,
    getSurface,
    setActiveMenuItem,
    toggleDarkMode
  }
}
